import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, euro, errMsg } from "@/lib/api";
import { useSeo, SITE_URL } from "@/lib/seo";
import { EXTREME, EXTREME_EU } from "@/lib/subscription";
import { useProduct } from "@/hooks/useProducts";
import { ExtremeBanner, ExtremeBenefits } from "@/components/subscription/ExtremeOffer";
import { RELAY_METHODS, ShippingSelector, useShippingMethods } from "@/components/site/ShippingSelector";
import { CustomerForm, EMPTY_CUSTOMER, customerErrors } from "@/components/checkout/CustomerForm";
import { SecureNote } from "@/components/site/Trust";
import { landingRef } from "@/components/site/VisitTracker";
import { Faq } from "@/components/site/Faq";

const P = euro(EXTREME.price);
const S = euro(EXTREME.shipping);
const T = euro(EXTREME.total);
export const EXTREME_CONDITIONS = `Abonnement mensuel à reconduction automatique : ${P} pour le flacon Extrême Repouss 50 ml + ${S} de livraison en point relais Mondial Relay, soit ${T} prélevés chaque mois par carte bancaire, à la date anniversaire de la souscription. Un flacon de 50 ml est expédié après chaque prélèvement. Sans engagement : pause ou résiliation à tout moment depuis « Mon abonnement » (lien envoyé par email) ; la résiliation arrête les prélèvements et les envois suivants, le mois déjà réglé reste expédié. Livraison en France métropolitaine et en Europe (${EXTREME_EU}).`;

const DETAILS = [
  ["Produit", "Extrême Repouss, flacon de 50 ml (30 ml + 20 ml offerts)"],
  ["Prix du produit", `${P} par mois`],
  ["Livraison", `${S} par mois, en point relais ou locker Mondial Relay`],
  ["Total mensuel", `${T} par mois, livraison comprise`],
  ["Fréquence", "Un flacon de 50 ml chaque mois, expédié après chaque prélèvement"],
  ["Délais", "3 à 5 jours ouvrés en France, 4 à 8 jours ouvrés en Europe"],
  ["Zones", `France métropolitaine et Europe : ${EXTREME_EU}`],
  ["Renouvellement", "Automatique chaque mois à la date de souscription, par carte bancaire"],
  ["Résiliation", "Sans engagement, à tout moment depuis « Mon abonnement »"],
];

const FAQ = [
  { q: "Comment fonctionne l'Abonnement Extrême Repouss ?", a: `Vous vous abonnez une fois : chaque mois, ${P} pour le flacon de 50 ml et ${S} de livraison sont prélevés par carte, soit ${T}. Votre flacon part ensuite vers le point relais Mondial Relay que vous avez choisi.` },
  { q: "Combien vais-je payer exactement ?", a: `${T} par mois : ${P} pour le produit + ${S} pour la livraison en point relais. Aucun autre frais n'est ajouté. Le montant est rappelé avant la validation du paiement.` },
  { q: "Quand suis-je prélevée ?", a: "Le premier prélèvement a lieu le jour de la souscription, puis chaque mois à la même date. Un email vous prévient quelques jours avant chaque renouvellement." },
  { q: "Dans quels pays puis-je recevoir mon abonnement ?", a: `En France métropolitaine et dans les pays européens desservis par Mondial Relay : ${EXTREME_EU}. Le même tarif de livraison de ${S} s'applique.` },
  { q: "Comment résilier ou mettre en pause ?", a: "L'abonnement est sans engagement. Depuis « Mon abonnement » (lien reçu par email, ou redemandé avec votre adresse email), vous pouvez mettre en pause ou résilier en un clic. La résiliation arrête les prélèvements et les envois suivants." },
  { q: "Puis-je changer de point relais ?", a: "Oui : répondez simplement à l'un de nos emails ou contactez-nous avant votre prochain prélèvement, nous mettons à jour votre point relais." },
  { q: "Puis-je acheter le flacon de 50 ml sans abonnement ?", a: `Oui, l'Extrême Repouss Voyage 50 ml est aussi vendu à l'unité au prix de ${euro(EXTREME.unit)}, frais de livraison selon votre panier.` },
];

const LD = {
  "@context": "https://schema.org",
  "@graph": [
    { "@type": "Product", name: "Abonnement Extrême Repouss 50 ml", brand: { "@type": "Brand", name: "Ozentya" }, image: `${SITE_URL}${EXTREME.image}`,
      description: `Le flacon Extrême Repouss 50 ml chaque mois : ${P} par mois + ${S} de livraison en point relais Mondial Relay, soit ${T} par mois.`,
      offers: { "@type": "Offer", price: EXTREME.price, priceCurrency: "EUR", availability: "https://schema.org/InStock", url: `${SITE_URL}${EXTREME.path}`,
        priceSpecification: { "@type": "UnitPriceSpecification", price: EXTREME.price, priceCurrency: "EUR", referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitCode: "MON" } },
        shippingDetails: { "@type": "OfferShippingDetails", shippingRate: { "@type": "MonetaryAmount", value: EXTREME.shipping, currency: "EUR" } } } },
    { "@type": "FAQPage", mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
  ],
};

function useHashScroll() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return undefined;
    const t = setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    return () => clearTimeout(t);
  }, [hash]);
}

function ProductPresentation() {
  const { data: p } = useProduct(EXTREME.slug);
  return (
    <section data-testid="extreme-product" className="mt-24 grid gap-10 lg:grid-cols-2 lg:gap-16">
      <div className="overflow-hidden rounded-sm border border-[var(--line)] bg-[var(--ink-2)]">
        <img src={EXTREME.photo} alt="Extrême Repouss Lotion 50 ml, 30 ml + 20 ml offerts" width="848" height="1264" loading="lazy" decoding="async" className="aspect-[4/5] h-full w-full object-cover" />
      </div>
      <div>
        <p className="eyebrow mb-4">Le produit</p>
        <h2 className="font-display text-3xl leading-tight md:text-5xl">Extrême Repouss, <span className="italic gold-text">format 50 ml</span></h2>
        <p data-testid="extreme-product-description" className="mt-5 text-base leading-relaxed text-[var(--parchment)]/90">{p?.description || "La lotion Extrême Repouss d'Ozentya en flacon de 50 ml avec embout applicateur, à appliquer sur le cuir chevelu."}</p>
        {p?.usage ? <p className="mt-4 text-sm leading-relaxed text-[var(--parchment)]/85"><b className="text-[var(--parchment)]">Utilisation : </b>{p.usage}</p> : null}
        <dl data-testid="extreme-details" className="mt-8 divide-y divide-[var(--line)] border-y border-[var(--line)]">
          {DETAILS.map(([k, v]) => (
            <div key={k} className="grid gap-1 py-3 sm:grid-cols-[150px_1fr] sm:gap-4"><dt className="text-xs uppercase tracking-[0.16em] text-[var(--gold-soft)]">{k}</dt><dd className="text-sm text-[var(--parchment)]/90">{v}</dd></div>
          ))}
        </dl>
        <Link to={`/produit/${EXTREME.slug}`} data-testid="extreme-unit-link" className="mt-5 inline-block text-xs text-[var(--gold-soft)] underline underline-offset-4">Acheter le flacon 50 ml à l'unité ({euro(EXTREME.unit)})</Link>
      </div>
    </section>
  );
}

function Summary({ loading, onPay, accepted, setAccepted }) {
  return (
    <aside data-testid="extreme-summary" className="rounded-sm border border-[var(--gold)]/60 bg-[var(--ink-2)] p-6">
      <p className="eyebrow mb-4">Récapitulatif mensuel</p>
      <p className="flex justify-between gap-3 border-b border-[var(--line)] py-3 text-sm"><span>Extrême Repouss 50 ml</span><span data-testid="extreme-summary-product">{P}</span></p>
      <p className="flex justify-between gap-3 border-b border-[var(--line)] py-3 text-sm"><span>Livraison point relais Mondial Relay</span><span data-testid="extreme-summary-shipping">{S}</span></p>
      <div className="mt-4 flex items-baseline justify-between"><span className="text-sm">Total par mois</span><span data-testid="extreme-summary-total" className="font-display text-4xl text-[var(--gold-soft)]">{T}</span></div>
      <p data-testid="extreme-billing-note" className="mt-3 text-xs leading-relaxed text-[var(--parchment)]/85">Premier paiement de {T} aujourd'hui, puis {T} prélevés automatiquement chaque mois à la même date. Un flacon de 50 ml expédié après chaque prélèvement. Sans engagement : pause ou résiliation à tout moment depuis « Mon abonnement ».</p>
      <label data-testid="extreme-accept" className="mt-4 flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-[var(--parchment)]/90">
        <input type="checkbox" data-testid="extreme-accept-checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--gold)]" />
        <span>J'accepte les conditions de l'abonnement (renouvellement mensuel automatique de {T}, résiliable à tout moment) et les <Link to="/cgv" data-testid="extreme-cgv-link" className="underline underline-offset-4">conditions générales de vente</Link>.</span>
      </label>
      <button data-testid="extreme-pay-button" onClick={onPay} disabled={loading}
        className="mt-5 flex min-h-14 w-full items-center justify-center gap-2 rounded-full bg-[var(--gold)] px-4 py-3 text-sm font-bold uppercase tracking-[0.16em] text-[var(--ink)] transition-[background-color,transform] duration-300 hover:bg-[var(--gold-soft)] active:scale-[0.98] disabled:opacity-40">
        {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirection sécurisée…</> : `Je m'abonne · ${T} / mois`}
      </button>
      <div className="mt-3"><SecureNote testid="extreme-secure-note" /></div>
    </aside>
  );
}

function MobileBar({ hidden }) {
  if (hidden) return null;
  return (
    <div data-testid="extreme-mobile-bar" className="fixed inset-x-0 bottom-0 z-[45] flex items-center gap-3 border-t border-[var(--gold)]/50 bg-[var(--ink)]/95 px-4 py-3 backdrop-blur-md md:hidden">
      <span className="flex-1 text-xs leading-tight"><b className="block font-display text-xl text-[var(--gold-soft)]">{P} / mois</b>+ {S} livraison = {T}</span>
      <a href="#souscrire" data-testid="extreme-mobile-cta" onClick={(e) => { e.preventDefault(); document.getElementById("souscrire")?.scrollIntoView({ behavior: "smooth" }); }}
        className="inline-flex h-12 items-center rounded-full bg-[var(--gold)] px-5 text-xs font-bold uppercase tracking-[0.16em] text-[var(--ink)]">Je m'abonne</a>
    </div>
  );
}

function useInView(ref) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver(([e]) => setOn(e.isIntersecting), { threshold: 0.05 });
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  return on;
}

export default function ExtremeSubscription() {
  const [params] = useSearchParams();
  const { data: allMethods } = useShippingMethods(EXTREME.price);
  const methods = (Array.isArray(allMethods) ? allMethods : []).filter((m) => RELAY_METHODS.includes(m.id)).map((m) => ({ ...m, amount: EXTREME.shipping }));
  const [method, setMethod] = useState("mondial_relay");
  const [relay, setRelay] = useState(null);
  const [customer, setCustomer] = useState(EMPTY_CUSTOMER);
  const [showErrors, setShowErrors] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);
  const formVisible = useInView(formRef);
  const errors = customerErrors(customer);
  useHashScroll();
  useSeo({ title: `Abonnement Extrême Repouss 50 ml : ${P}/mois + ${S} de livraison | Ozentya`,
    description: `Recevez chaque mois votre Extrême Repouss 50 ml en point relais Mondial Relay : ${P} par mois + ${S} de livraison, soit ${T}. France et Europe, sans engagement.`,
    path: EXTREME.path, image: EXTREME.image, jsonLd: LD });

  useEffect(() => { if (params.get("annule")) toast("Paiement annulé : aucun montant n'a été débité."); }, [params]);
  useEffect(() => {
    const reset = (e) => { if (e.persisted) setLoading(false); };
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, []);

  const pay = async () => {
    setShowErrors(true);
    if (Object.keys(errors).length) { toast.error("Merci de compléter vos coordonnées."); formRef.current?.scrollIntoView({ behavior: "smooth" }); return; }
    if (!relay) { toast.error("Choisissez votre point relais ou locker Mondial Relay."); document.querySelector('[data-testid="shipping-selector"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); return; }
    if (!accepted) { toast.error("Merci d'accepter les conditions de l'abonnement."); return; }
    setLoading(true);
    try {
      const { data: res } = await api.post("/subscriptions/checkout", { plan: "extreme", products: [EXTREME.slug], origin_url: window.location.origin,
        shipping_method: method, relay, customer: { ...customer, country: relay.country || "FR" }, visit_ref: landingRef() });
      if (res?.checkout_url) window.location.href = res.checkout_url;
      else setLoading(false);
    } catch (e) {
      toast.error(errMsg(e));
      setLoading(false);
    }
  };

  return (
    <main data-testid="extreme-subscription-page" className="mx-auto max-w-6xl px-5 pb-32 pt-36 md:px-10 md:pb-24">
      <ExtremeBanner placement="subscription" testid="extreme-hero" headingLevel="h1" ctaTo="#souscrire" />
      <section className="mt-12"><ExtremeBenefits /></section>
      <ProductPresentation />
      <section id="souscrire" ref={formRef} data-testid="extreme-form" className="mt-24 scroll-mt-32">
        <p className="eyebrow mb-4">Je m'abonne</p>
        <h2 className="font-display text-3xl leading-tight md:text-5xl">Votre abonnement en <span className="italic gold-text">2 minutes</span></h2>
        <ul className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[var(--parchment)]/90">
          {["Coordonnées", "Point relais Mondial Relay", "Récapitulatif et paiement sécurisé"].map((s, i) => <li key={s} className="flex items-center gap-2"><Check className="h-4 w-4 text-[var(--gold)]" />{i + 1}. {s}</li>)}
        </ul>
        <div className="mt-10 grid gap-12 lg:grid-cols-[1fr_380px]">
          <div className="min-w-0 space-y-12">
            <section><p className="eyebrow mb-4">1. Mes coordonnées</p><CustomerForm value={customer} onChange={setCustomer} errors={errors} showErrors={showErrors} /></section>
            <section><p className="mb-3 text-sm text-[var(--parchment)]/85">2. Choisissez votre point relais (France ou Europe), même tarif de {S} :</p>
              <ShippingSelector method={method} setMethod={setMethod} relay={relay} setRelay={setRelay} methods={methods} /></section>
          </div>
          <div className="lg:sticky lg:top-36 lg:self-start">
            <Summary loading={loading} onPay={pay} accepted={accepted} setAccepted={setAccepted} />
            <p className="mt-4 text-xs text-[var(--parchment)]/80">Déjà abonnée ? <Link to="/mon-abonnement" data-testid="extreme-manage-link" className="text-[var(--gold-soft)] underline underline-offset-4">Gérer mon abonnement</Link></p>
          </div>
        </div>
      </section>
      <p data-testid="extreme-conditions" className="mt-12 text-xs leading-relaxed text-[var(--parchment)]/80">{EXTREME_CONDITIONS}</p>
      <p className="mt-4 text-sm"><Link to="/abonnement" data-testid="extreme-other-plans" className="text-[var(--gold-soft)] underline underline-offset-4">Voir aussi les abonnements Duo et Famille (soins au choix)</Link></p>
      <Faq items={FAQ} id="extreme-faq" title="Questions fréquentes sur l'Abonnement Extrême Repouss" />
      <MobileBar hidden={formVisible} />
    </main>
  );
}
